import { useSyncExternalStore } from 'react'

export type TimelineFilters = {
  families: string[]
  kinds: string[]
  owner: string
  from: string
  to: string
  q: string
  sort: string
}
export type TimelineFacets = {
  families: string[]
  kinds?: Record<string, string[]>
  sources?: { value: string; label: string }[]
}
const scalarKeys = ['owner', 'from', 'to', 'q', 'sort'] as const
const arrayKeys = ['families', 'kinds'] as const

export function emptyTimelineFilters(): TimelineFilters {
  return { families: [], kinds: [], owner: '', from: '', to: '', q: '', sort: 'newest' }
}

export function readTimelineFilters(search: string): TimelineFilters {
  const params = new URLSearchParams(search)
  const filters = emptyTimelineFilters()
  for (const key of scalarKeys) filters[key] = params.get(key) ?? filters[key]
  for (const key of arrayKeys) filters[key] = params.getAll(`${key}[]`)
  if (filters.families.length !== 1) filters.kinds = []
  return filters
}

export function timelineParams(filters: TimelineFilters, params = new URLSearchParams()) {
  for (const key of scalarKeys) {
    params.delete(key)
    if (filters[key]) params.set(key, filters[key])
  }
  for (const key of arrayKeys) {
    params.delete(`${key}[]`)
    for (const value of filters[key]) params.append(`${key}[]`, value)
  }
  return params
}

export function subscribeLocation(listener: (event: PopStateEvent) => void) {
  window.addEventListener('popstate', listener)
  return () => window.removeEventListener('popstate', listener)
}

export function pushLocation(url: URL, resetSearch = false) {
  if (url.href === window.location.href) return
  window.history.pushState({}, '', url)
  // Mark only this notification, never the stored history entry: browser Back
  // restores its saved controls, while a facet pick preserves a pending search.
  window.dispatchEvent(
    new PopStateEvent('popstate', { state: { contactQueryChange: !resetSearch } }),
  )
}

export function useTimelineFilters() {
  const search = useSyncExternalStore(subscribeLocation, () => window.location.search)
  const filters = readTimelineFilters(search)
  const change = (next: TimelineFilters, resetSearch = false) => {
    const url = new URL(window.location.href)
    const params = timelineParams(next, url.searchParams)
    if (next.sort === 'newest') params.delete('sort')
    params.set('tab', 'timeline')
    url.search = params.toString()
    pushLocation(url, resetSearch)
  }
  return { filters, change, clear: () => change(emptyTimelineFilters(), true) }
}

export function hasTimelineFilters(filters: TimelineFilters) {
  return Boolean(
    filters.q ||
    filters.owner ||
    filters.from ||
    filters.to ||
    filters.families.length ||
    filters.kinds.length,
  )
}

export const familyLabels: Record<string, string> = {
  events: 'Events',
  learning: 'Learning',
  money: 'Money',
  email: 'Email',
  portal: 'Portal',
  membership: 'Membership',
  relationships: 'Relationships',
  profile_changes: 'Profile changes',
  notes: 'Notes',
}
const kindLabels: Record<string, string> = {
  learning_enrolled: 'Enrolled',
  learning_completed: 'Completed',
  learning_failed: 'Failed',
  learning_withdrawn: 'Withdrew',
  event_registration: 'Registered',
  event_cancellation: 'Cancelled',
  event_check_in: 'Checked in / undone',
  email_sent: 'Sent',
  email_bounced: 'Bounced',
  email_complained: 'Spam complaint',
  email_failed: 'Failed to send',
  email_clicked: 'Clicked a link',
  note_created: 'Note added',
  portal_sign_in: 'Signed in',
  membership_status_change: 'Status changed',
}
export function kindLabel(kind: string) {
  return (
    kindLabels[kind] ?? kind.replaceAll('_', ' ').replace(/^./, (letter) => letter.toUpperCase())
  )
}
